import {window} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import Config from 'config';

export const SearchTileUtils = gmaps => ({
    savedZoom: null,

    previousInnerCoords: null,

    getBoundingCoordinates(latLngBounds) {
        // https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds
        const ne = latLngBounds.getNorthEast();
        const sw = latLngBounds.getSouthWest();
        const nw = new gmaps.LatLng(ne.lat(), sw.lng());
        const se = new gmaps.LatLng(sw.lat(), ne.lng());

        return {
            ne,
            sw,
            nw,
            se,
            // round lat/lng to 5 decimals (provides accuracy to roughly 1.11 m)
            // re: http://stackoverflow.com/questions/7167604/how-accurately-should-i-store-latitude-and-longitude
            path: `(${nw.lat().toFixed(5)},${nw
                .lng()
                .toFixed(5)}),(${ne.lat().toFixed(5)},${ne
                .lng()
                .toFixed(5)}),(${se.lat().toFixed(5)},${se
                .lng()
                .toFixed(5)}),(${sw.lat().toFixed(5)},${sw
                .lng()
                .toFixed(5)}),(${nw.lat().toFixed(5)},${nw.lng().toFixed(5)})`,
        };
    },

    getTileData(tile, map) {
        const currentTilePathObj = this.getBoundingCoordinates(tile);

        // display tiles on map with random color
        if (Config.runningTests) {
            const currentTilePolygon = new gmaps.Polygon({
                paths: [
                    currentTilePathObj.ne,
                    currentTilePathObj.se,
                    currentTilePathObj.sw,
                    currentTilePathObj.nw,
                ],
                fillColor: `#${Math.floor(Math.random() * 16777215).toString(
                    16
                )}`,
            });

            currentTilePolygon.setMap(map);
        }

        return {
            // eslint-disable-next-line camelcase
            outer_coords: currentTilePathObj.path,
        };
    },

    createTiles(bounds, searchState) {
        const {zoom} = searchState;
        const {
            ne: boundsNE,
            nw: boundsNW,
            se: boundsSE,
            path: boundsPath,
        } = this.getBoundingCoordinates(bounds);
        const mapWidth = boundsNE.lng() - boundsNW.lng();
        const mapHeight = boundsNE.lat() - boundsSE.lat();
        const preparedData = {};
        const tiles = [];
        let tileCount;
        let tilesHigh;
        let tileWidth;
        let tileHeight;
        let xIncrement = 0;
        let yIncrement = 0;
        let i = 1;

        // if zooming out include previous bounds as inner coords
        if (this.savedZoom > zoom) {
            // zooming out
            preparedData.innerCoords = this.previousInnerCoords;
        }

        // store current coords to remove from next zoom out search
        this.previousInnerCoords = boundsPath;

        // adjust number of tiles and tile sizes based on current zoom level
        if (zoom < 16) {
            tileCount = 6;
            tilesHigh = 2;
            tileWidth = mapWidth / 3;
            tileHeight = mapHeight / 2;
        } else if (zoom < 18) {
            tileCount = 4;
            tilesHigh = 2;
            tileWidth = mapWidth / 2;
            tileHeight = mapHeight / 2;
        } else if (zoom < 20) {
            tileCount = 2;
            tilesHigh = 1;
            tileWidth = mapWidth / 2;
            tileHeight = mapHeight;
        } else {
            tileCount = 1;
            tilesHigh = 1;
            tileWidth = mapWidth;
            tileHeight = mapHeight;
        }

        // create tiles
        for (i; i <= tileCount; i++) {
            let tileOffsetY;
            let tileOffsetX;

            // tiles with multiples of tilesHigh have y offset 0, x offset is tileWidth * (index * tilesHigh)
            if (i % tilesHigh === 0) {
                tileOffsetY = tileHeight * yIncrement;
                tileOffsetX = tileWidth * xIncrement;
                xIncrement++;
                yIncrement = 0;
                // odd number tiles have y offset of one tileHeight, x offset increments each odd tile
            } else {
                tileOffsetY = tileHeight * yIncrement;
                tileOffsetX = tileWidth * xIncrement;
                yIncrement++;
            }

            const northLat = bounds.getNorthEast().lat() - tileOffsetY;
            const westLng = bounds.getSouthWest().lng() + tileOffsetX;
            const southLat = northLat - tileHeight;
            const eastLng = westLng + tileWidth;

            // push tile LatLngBounds object to tiles array
            tiles.push(
                new gmaps.LatLngBounds(
                    new gmaps.LatLng(southLat, westLng),
                    new gmaps.LatLng(northLat, eastLng)
                )
            );
        }

        this.savedZoom = zoom;
        preparedData.numRequests = tileCount;
        preparedData.tiles = tiles;

        return preparedData;
    },
});

export default SearchTileUtils(
    EnvironmentUtils.isBrowser() ? window.google.maps : null
);
